import React, { useState, MouseEvent } from 'react';
import MuiButton from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { SxProps, Theme } from '@mui/material';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';

export interface IButton {
  label?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'link';
  dialogType?: DIALOG_TYPE;
  hasIcon?: boolean;
  icon?: React.ReactNode;
  width?: string;
  disabled?: boolean;
  selected?: boolean;
  type?: 'button' | 'submit';
  hasDropdown?: boolean;
  dropdownItems?: { label: string; onClick: () => void }[];
  className?: string;
  sx?: SxProps<Theme>;
}

export const Button = ({
  label = '',
  children,
  onClick,
  variant = 'primary',
  dialogType,
  hasIcon,
  icon,
  width,
  selected,
  type = 'button',
  disabled = false,
  hasDropdown = false,
  dropdownItems = [],
  className = '',
  sx,
}: IButton) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isDropdownOpen = Boolean(anchorEl);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (hasDropdown) {
      setAnchorEl(event.currentTarget);
    } else {
      onClick?.();
    }
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  // Base MUI SX styles
  const baseSx: SxProps<Theme> = {
    minHeight: 36,
    px: 1.75,
    py: 0.75,
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: '10px',
    textTransform: 'none',
    letterSpacing: '0.01em',
    width: width || (className.includes('w-full') ? '100%' : 'auto'),
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:active': {
      transform: 'scale(0.98)',
    },
  };

  // Resolve styling based on variant & dialogType
  let resolvedSx: SxProps<Theme> = {};

  if (variant === 'link') {
    const isError = dialogType === 'error';
    resolvedSx = {
      ...baseSx,
      color: isError ? '#f31260' : 'var(--brand-primary, #6366f1)',
      bgcolor: 'transparent',
      boxShadow: 'none',
      border: 'none',
      px: 1,
      py: 0.5,
      '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
        color: isError ? '#f31260' : 'inherit',
      },
      '&:hover': {
        bgcolor: isError
          ? 'rgba(243, 18, 96, 0.08)'
          : 'var(--brand-primary-light, rgba(99, 102, 241, 0.08))',
        color: isError ? '#e10e55' : undefined,
        boxShadow: 'none',
        '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
          color: isError ? '#e10e55' : 'inherit',
        },
      },
      '&.Mui-disabled': {
        color: 'text.disabled',
      },
    };
  } else if (variant === 'secondary') {
    if (dialogType === 'warning') {
      resolvedSx = {
        ...baseSx,
        bgcolor: 'warning.50',
        color: 'warning.dark',
        border: '1px solid',
        borderColor: 'warning.200',
        '&:hover': {
          bgcolor: 'warning.100',
          borderColor: 'warning.300',
        },
      };
    } else if (dialogType === 'success') {
      resolvedSx = {
        ...baseSx,
        bgcolor: 'success.50',
        color: 'success.dark',
        border: '1px solid',
        borderColor: 'success.200',
        '&:hover': {
          bgcolor: 'success.100',
          borderColor: 'success.300',
        },
      };
    } else if (dialogType === 'error') {
      resolvedSx = {
        ...baseSx,
        bgcolor: 'rgba(243, 18, 96, 0.05)',
        color: '#f31260',
        border: '1px solid',
        borderColor: 'rgba(243, 18, 96, 0.35)',
        '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
          color: '#f31260',
        },
        '&:hover': {
          bgcolor: 'rgba(243, 18, 96, 0.1)',
          borderColor: '#f31260',
          color: '#e10e55',
          '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
            color: '#e10e55',
          },
        },
      };
    } else if (dialogType === 'info') {
      resolvedSx = {
        ...baseSx,
        bgcolor: 'var(--brand-primary-light, rgba(99, 102, 241, 0.08))',
        color: 'var(--brand-primary, #6366f1)',
        border: '1px solid',
        borderColor: 'rgba(99, 102, 241, 0.2)',
        '&:hover': {
          bgcolor: 'var(--brand-primary-light, rgba(99, 102, 241, 0.15))',
        },
      };
    } else {
      resolvedSx = {
        ...baseSx,
        bgcolor: '#ffffff',
        color: 'slate.700',
        border: '1px solid',
        borderColor: 'slate.300',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '&:hover': {
          bgcolor: 'grey.50',
          borderColor: 'slate.400',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.05)',
        },
      };
    }
  } else {
    // Primary - avoid gradients, use solid background and contrast text
    if (dialogType === 'warning') {
      resolvedSx = {
        ...baseSx,
        bgcolor: '#f59e0b',
        backgroundImage: 'none',
        color: '#ffffff',
        boxShadow: '0 2px 8px 0 rgba(245, 158, 11, 0.25)',
        '&:hover': {
          bgcolor: '#d97706',
          backgroundImage: 'none',
          boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.35)',
        },
      };
    } else if (dialogType === 'success') {
      resolvedSx = {
        ...baseSx,
        bgcolor: '#10b981',
        backgroundImage: 'none',
        color: '#ffffff',
        boxShadow: '0 2px 8px 0 rgba(16, 185, 129, 0.25)',
        '&:hover': {
          bgcolor: '#059669',
          backgroundImage: 'none',
          boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
        },
      };
    } else if (dialogType === 'error') {
      resolvedSx = {
        ...baseSx,
        bgcolor: '#f31260',
        backgroundImage: 'none',
        color: '#ffffff',
        boxShadow: '0 2px 8px 0 rgba(243, 18, 96, 0.25)',
        '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
          color: '#ffffff',
        },
        '&:hover': {
          bgcolor: '#e10e55',
          backgroundImage: 'none',
          boxShadow: '0 4px 14px 0 rgba(243, 18, 96, 0.35)',
        },
      };
    } else {
      resolvedSx = {
        ...baseSx,
        bgcolor: 'var(--brand-primary, #038912)',
        backgroundImage: 'none',
        color: 'var(--brand-text-on-primary, #ffffff)',
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12)',
        '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
          color: 'var(--brand-text-on-primary, #ffffff)',
        },
        '&:hover': {
          bgcolor: 'var(--brand-primary-hover, #026b0e)',
          backgroundImage: 'none',
          color: 'var(--brand-text-on-primary, #ffffff)',
          boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.18)',
        },
      };
    }
  }

  return (
    <Box
      className={className}
      sx={{
        display: className.includes('w-full') ? 'block' : 'inline-block',
        width: className.includes('w-full') ? '100%' : 'auto',
      }}
    >
      <MuiButton
        type={type}
        disabled={disabled}
        onClick={handleButtonClick}
        aria-selected={selected}
        startIcon={hasIcon || icon ? icon : undefined}
        endIcon={
          hasDropdown ? (
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 18,
                transition: 'transform 0.2s ease',
                transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          ) : undefined
        }
        sx={{
          ...resolvedSx,
          ...sx,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {children ?? label}
        </span>
      </MuiButton>

      {hasDropdown && dropdownItems.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={isDropdownOpen}
          onClose={handleCloseDropdown}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '12px',
              minWidth: 180,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {dropdownItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                item.onClick();
                handleCloseDropdown();
              }}
              sx={{
                py: 1,
                px: 2,
                fontSize: '0.85rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default Button;
